/**
 * EmailOutboundPage — admin surface for per-tenant SMTP outbound email config.
 *
 * Multiple configs per tenant with is_default flag. Only one can be default.
 * Full CRUD operations with list and form views.
 *
 * Permission: `admin:integrations:view` (ADMIN_INTEGRATIONS_VIEW) for view, `admin:integrations:manage` (ADMIN_INTEGRATIONS_MANAGE) for manage.
 */
import { useState, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Column } from 'primereact/column'
import AdminListScreenLayout from '../../../components/layouts/AdminListScreenLayout.js'
import DataTable from '../../../components/ui/DataTable.js'
import { api } from '../../../api/client.js'
import { useConfirmDialog } from '../../../components/ui/ConfirmDialog.js'
import { Mail, Eye, Pencil, Trash2 } from 'lucide-react'
import SummaryCards from '../../../components/admin/SummaryCards.js'
import ListToolbar from '../../../components/admin/ListToolbar.js'
import { formatDateTime } from '../../../utils/formatDate.js'
export interface EmailOutboundConfig {
  id: string
  name: string | null
  description: string | null
  smtpHost: string
  smtpPort: number
  smtpUser: string
  fromAddress: string
  fromName: string | null
  authEnabled: boolean
  oauthProvider: string | null
  connectionTimeout: number
  authTimeout: number
  encryptionEnabled: boolean
  deleteAfterFetch: boolean
  markSeen: boolean
  ignoreCc: boolean
  status: string
  isDefault: boolean
  hasPassword: boolean
  createdAt: string
  monitorRules?: Array<{
    id: string
    to: string
    cc: string | null
    campaign: string
    campaignName: string | null
  }>
}

export default function EmailOutboundPage(): JSX.Element {
  const navigate = useNavigate()
  const [items, setItems] = useState<EmailOutboundConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { confirm, Dialog } = useConfirmDialog()

  // These configs are fetched all-at-once (no server pagination), so the
  // summary counts and search filter are both derived client-side from the
  // full items array.
  const filteredItems = items.filter((item) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (item.name ?? item.smtpHost).toLowerCase().includes(q) || item.smtpHost.toLowerCase().includes(q) || item.fromAddress.toLowerCase().includes(q)
  })
  const summary = {
    total: items.length,
    active: items.filter((i) => i.status === 'ACTIVE').length,
    inactive: items.filter((i) => i.status !== 'ACTIVE').length,
  }

  const fetchItems = async () => {
    setLoading(true)
    try {
      const response = await api.get<{ items: EmailOutboundConfig[] }>('/admin/channels/email/outbound')
      setItems(response.items || [])
    } catch (error) {
      console.error('Error fetching email outbound configs:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchItems()
  }, [])

  const handleAdd = () => {
    navigate('/admin/channels/email/outbound/new')
  }

  const handleView = (id: string) => {
    navigate(`/admin/channels/email/outbound/${id}?view=true`)
  }

  const handleEdit = (id: string) => {
    navigate(`/admin/channels/email/outbound/${id}`)
  }

  const handleDelete = async (id: string) => {
    confirm({
      title: 'Delete Email Outbound Config',
      message: 'Are you sure you want to delete this email outbound configuration?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/channels/email/outbound/${id}`)
          void fetchItems()
        } catch (error) {
          console.error('Error deleting email outbound config:', error)
        }
      },
    })
  }

  const actionsBodyTemplate = (rowData: { id: string }): JSX.Element => {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleView(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="View"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => handleEdit(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => handleDelete(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-red-500 transition-all shrink-0"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  const statusBodyTemplate = (rowData: { status: string }): JSX.Element => {
    return (
      <span className={`px-2 py-1 rounded text-xs ${rowData.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {rowData.status}
      </span>
    )
  }

  const isDefaultBodyTemplate = (rowData: { isDefault: boolean }): JSX.Element => {
    return (
      <span className={`px-2 py-1 rounded text-xs ${rowData.isDefault ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
        {rowData.isDefault ? 'Default' : '-'}
      </span>
    )
  }

  return (
    <AdminListScreenLayout>
      <div className="w-full space-y-6 px-4 pb-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Email Outbound (SMTP)</h1>
            <p className="text-sm text-gray-400 mt-1">Configure and manage SMTP outbound email configurations.</p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Mail className="h-4 w-4" />
            Add Email Outbound
          </button>
        </div>

        {/* Summary cards */}
        <SummaryCards
          cards={[
            { label: 'Total Configs', value: summary.total, accentColor: '#6366f1' },
            { label: 'Active', value: summary.active, accentColor: '#10b981' },
            { label: 'Inactive', value: summary.inactive, accentColor: '#ef4444' },
          ]}
        />

        {/* Toolbar */}
        <ListToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={() => { setSearchQuery(''); void fetchItems() }}
          allButtonLabel="All Email Outbound"
          placeholder="Search by name, host, or from address"
          loading={loading}
        />

        {/* Table */}
        <DataTable value={filteredItems} loading={loading} emptyMessage="No email outbound configurations found." className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100">
          <Column
            header="Email Outbound"
            body={(rowData: any) => (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-[#3454cf]" />
                </div>
                <p className="text-sm font-semibold text-gray-900">{rowData.name || rowData.smtpHost}</p>
              </div>
            )}
            // style={{ width: '10%' }}
          />
          <Column
            field="smtpHost"
            header="Host"
          />
          <Column
            field="smtpPort"
            header="Port"
          />
          <Column
            field="smtpUser"
            header="Username"
          />
          <Column
            field="fromAddress"
            header="From Address"
          />
          {/* <Column
            field="fromName"
            header="From Name"
          /> */}
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
          />
          <Column
            field="isDefault"
            header="Default"
            body={isDefaultBodyTemplate}
          />
          <Column
            field="createdAt"
            header="Created At"
            body={(rowData: any) => formatDateTime(rowData.createdAt)}
          />
          <Column
            field="actions"
            header="Actions"
            body={actionsBodyTemplate}
            style={{ width: '120px' }}
          />
        </DataTable>
      </div>
      <Dialog />
    </AdminListScreenLayout>
  )
}
